import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { User, Star, Edit, Save, X, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import ProfilePictureUpload from './ProfilePictureUpload';
import AdvisorReviews from './AdvisorReviews';

interface AdvisorProfileProps {
  onProfileUpdate?: () => void;
}

const AdvisorProfile: React.FC<AdvisorProfileProps> = ({ onProfileUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    displayName: '',
    specialty: '',
    bio: '',
    experienceYears: 0,
    profileImageUrl: '',
    perMessageRate: 0
  });
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [totalRatings, setTotalRatings] = useState<number>(0);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchRatings();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('display_name, specialty, bio, experience_years, profile_image_url, per_message_rate')
        .eq('id', user?.id)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        setProfile({
          displayName: data.display_name || '',
          specialty: data.specialty || '',
          bio: data.bio || '',
          experienceYears: data.experience_years || 0,
          profileImageUrl: data.profile_image_url || '',
          perMessageRate: data.per_message_rate || 0
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      if (!user) return;
      
      // Get all ratings for this advisor
      const { data: ratingsData, error: ratingsError } = await supabase
        .from('advisor_ratings')
        .select('rating')
        .eq('advisor_id', user.id);

      if (ratingsError) throw ratingsError;

      if (ratingsData && ratingsData.length > 0) {
        // Calculate average rating
        const sum = ratingsData.reduce((acc, curr) => acc + curr.rating, 0);
        const avg = sum / ratingsData.length;
        setAverageRating(avg);
        setTotalRatings(ratingsData.length);
        
        // Update the profile rating in the database
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            rating: avg,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
          
        if (updateError) {
          console.error('Error updating profile rating:', updateError);
        }
      } else {
        setAverageRating(null);
        setTotalRatings(0);
      }
    } catch (err) {
      console.error('Error fetching ratings:', err);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          display_name: profile.displayName,
          specialty: profile.specialty,
          bio: profile.bio,
          experience_years: profile.experienceYears,
          per_message_rate: profile.perMessageRate,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Also update advisor_settings
      const { error: settingsError } = await supabase
        .from('advisor_settings')
        .update({
          per_message_rate: profile.perMessageRate,
          updated_at: new Date().toISOString()
        })
        .eq('advisor_id', user.id);

      if (settingsError) {
        console.warn('Error updating advisor settings:', settingsError);
        // Continue anyway as the main profile was updated
      }

      setSuccess('Profile updated successfully');
      setEditing(false);
      
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpdate = (url: string) => {
    setProfile(prev => ({
      ...prev,
      profileImageUrl: url
    }));
    
    if (onProfileUpdate) {
      onProfileUpdate();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Your Profile</h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center text-purple-600 hover:text-purple-800"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit Profile
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center text-green-600 hover:text-green-800 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                fetchProfile(); // Reset to original values
              }}
              className="flex items-center text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 rounded-lg flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Picture */}
        <ProfilePictureUpload 
          currentImageUrl={profile.profileImageUrl}
          onImageUpdate={handleImageUpdate}
        />

        {/* Basic Info */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              {editing ? (
                <input
                  type="text"
                  value={profile.displayName}
                  onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              ) : (
                <p className="text-gray-800">{profile.displayName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialty
              </label>
              {editing ? (
                <input
                  type="text"
                  value={profile.specialty}
                  onChange={(e) => setProfile({ ...profile, specialty: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Tarot Reading, Mediumship, Astrology"
                />
              ) : (
                <p className="text-gray-800">{profile.specialty || 'Not specified'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience
              </label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={profile.experienceYears}
                  onChange={(e) => setProfile({ ...profile, experienceYears: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              ) : (
                <p className="text-gray-800">{profile.experienceYears} years</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating
              </label>
              <div className="flex items-center">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        averageRating && star <= Math.round(averageRating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="ml-2 text-gray-700">
                  {averageRating ? averageRating.toFixed(1) : 'No ratings yet'} 
                  {totalRatings > 0 && ` (${totalRatings} ${totalRatings === 1 ? 'review' : 'reviews'})`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Professional Bio
          </label>
          {editing ? (
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Tell clients about your background, abilities, and approach to readings..."
            />
          ) : (
            <p className="text-gray-800 whitespace-pre-line">{profile.bio || 'No bio provided'}</p>
          )}
        </div>

        {/* Pricing */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Per Message Rate ($)
            </label>
            {editing ? (
              <input
                type="number"
                min="0"
                step="0.01"
                value={profile.perMessageRate}
                onChange={(e) => setProfile({ ...profile, perMessageRate: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            ) : (
              <p className="text-gray-800">${profile.perMessageRate.toFixed(2)}</p>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Client Reviews</h3>
          <AdvisorReviews advisorId={user?.id || ''} onReviewsUpdated={fetchRatings} />
        </div>
      </div>
    </div>
  );
};

export default AdvisorProfile;